import React, { memo, useState, useEffect, FunctionComponent } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Form, Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Heading, Box, Text, InputField } from 'mdlkit';
import { capitalize } from 'lodash';
import { normalizeNumberOnlyWithLength } from '../../utils/register/normalize';
import PasswordField from './PasswordField';
import { EmailField, LegalLink } from './PatientForm';
import { CallCustomerService } from '../shared';
import { StyledForm, FormSubmitLoadingButton } from '../shared/Styles';
import {
  emailComposed,
  password,
  phoneNumberComposed,
} from '../../utils/register/validations';
import { required } from '../../utils/shared/validations';
import toast from '../../utils/toast';
import { V2PatientProfile } from '../../interfaces/patient';
import { PatientSubmitError } from '../../interfaces/register';
import getMonikerCheckAsync from '../../api/monikers/getMonikerCheckAsync';

const StyledBox = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-self: start;

  width: calc(100% + ${({ theme }) => theme.space.md}px);
  // 1A is 10% opacity
  background-color: ${({ theme }) => `${theme.primary}1A`};
  padding: ${({ theme }) => theme.space.sm}px;
  margin-left: -${({ theme }) => theme.space.sm}px;
  margin-right: -${({ theme }) => theme.space.sm}px;
`;

interface Props {
  onSubmit: (values: V2PatientProfile) => Promise<void | PatientSubmitError>;
  termsLink?: string;
  privacyLink?: string;
  monikerCheckId: string;
}

const V2MonikerPatientForm: FunctionComponent<Props> = ({
  onSubmit,
  termsLink,
  privacyLink,
  monikerCheckId,
}: Props) => {
  const { formatMessage: f } = useIntl();

  const [initialValues, setInitialValues] = useState<null | V2PatientProfile>(
    null
  );

  const getEligibleMember = async () => {
    const resp = await getMonikerCheckAsync(monikerCheckId);
    if ('error' in resp) {
      toast.error(
        <CallCustomerService message={f({ id: 'error.unexpectedError' })} />
      );
    } else {
      const {
        monikerCheck: { eligibleMember },
      } = resp;
      setInitialValues({
        ...eligibleMember,
        phone: '',
        password: '',
        acceptTerms: true,
      });
    }
  };

  useEffect(() => {
    getEligibleMember();
  }, []);

  return (
    initialValues && (
      <>
        <Heading as="h1" align="center">
          <FormattedMessage id="monikersRegister.heading" />
        </Heading>

        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={({
            handleSubmit,
            submitting,
            values,
            hasSubmitErrors,
            dirtySinceLastSubmit,
          }) => (
            <StyledForm onSubmit={handleSubmit} spellCheck={false}>
              <StyledBox as="dl">
                <Text as="dt">
                  <FormattedMessage id="name" />:
                </Text>
                <Text as="dd">
                  {capitalize(values.firstName)} {capitalize(values.lastName)}
                </Text>

                <Text as="dt">
                  <FormattedMessage id="dateOfBirth" />:
                </Text>
                <Text as="dd">
                  {new Intl.DateTimeFormat('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(
                    moment(
                      `${values.dobYear}-${values.dobMonth}-${values.dobDay}`,
                      'YYYY-MM-DD'
                    ).toDate()
                  )}{' '}
                </Text>

                <Text as="dt">
                  <FormattedMessage id="gender" />:
                </Text>
                <Text as="dd">{capitalize(values.gender)}</Text>

                <Text as="dt">
                  <FormattedMessage id="homeZipCode" />:
                </Text>
                <Text as="dd">{values.zip}</Text>
              </StyledBox>

              <Field id="firstName" name="firstName" component="input" hidden />
              <Field id="lastName" name="lastName" component="input" hidden />
              <Field id="gender" name="gender" component="input" hidden />
              <Field id="dobDay" name="dobDay" component="input" hidden />
              <Field id="dobMonth" name="dobMonth" component="input" hidden />
              <Field id="dobYear" name="dobYear" component="input" hidden />
              <Field id="zip" name="zip" component="input" hidden />
              <Field
                id="affiliationId"
                name="affiliationId"
                component="input"
                hidden
              />

              <Field
                id="email"
                label={f({ id: 'register.email' })}
                name="email"
                validate={emailComposed}
                parse={(value) => value && value.trim()}
                component={EmailField}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />

              <Field
                id="phone"
                label={f({ id: 'register.phone' })}
                name="phone"
                inputMode="numeric"
                validate={phoneNumberComposed}
                component={InputField}
                parse={(value: string) =>
                  normalizeNumberOnlyWithLength(value, 10)
                }
                aria-label="phone number"
              />

              <Field
                id="password"
                label={f({ id: 'register.password' })}
                name="password"
                validate={password}
                values={values}
                component={PasswordField}
              />

              <Box mt="sm">
                <Field
                  id="acceptTerms"
                  name="acceptTerms"
                  validate={required}
                  component="input"
                  hidden
                />
                <LegalLink termsLink={termsLink} privacyLink={privacyLink} />
              </Box>

              <FormSubmitLoadingButton
                id="createAccountBtn"
                type="submit"
                size="large"
                disabled={
                  submitting || (hasSubmitErrors && !dirtySinceLastSubmit)
                }
                isLoading={submitting}
              >
                <FormattedMessage id="register.createAccount" />
              </FormSubmitLoadingButton>
            </StyledForm>
          )}
        />
      </>
    )
  );
};

export default memo(V2MonikerPatientForm);
